import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { ArrowLeft, FileText, Download, AlertCircle, MessageSquare, Mail, ScrollText, BookOpen, ShieldCheck } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

interface ComplianceRuleResult {
  ruleId?: number;
  ruleName?: string;
  type?: string;
  status: string;
  severity?: string;
  message?: string;
  detail?: string | null;
}

interface ContractDetail {
  contract: { id: number; contractType: string; status: string; lifecycleState: string; documentKind: string; startDate: string | null; endDate: string | null; annualSalary?: number | null };
  coach: { id: number; name: string; sport: string } | null;
  compliance?: { ruleResults?: ComplianceRuleResult[]; summary?: { pass?: number; warn?: number; fail?: number } } | null;
  clauses: { id: number; position: number; clauseId: number; title: string | null; category: string | null; body: string | null }[];
  negotiationItems: { id: number; raisedBy: string; summary: string; status: string; createdAt: string }[];
  negotiationRounds: { id: number; createdAt: string; direction: string; recipientRole: string; emailStatus: string; emailSentAt: string | null }[];
  comments: { id: number; authorName: string; body: string; createdAt: string }[];
  documents: { id: number; fileName: string; mimeType: string; sizeBytes: number; uploadedAt: string }[];
  auditLog: { id: number; action: string; field: string | null; oldValue: string | null; newValue: string | null; actorName: string | null; createdAt: string }[];
}

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminContractDetail() {
  const params = useParams<{ id: string; contractId: string }>();
  const [, setLocation] = useLocation();
  const [data, setData] = useState<ContractDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`/api/admin/orgs/${params.id}/contracts/${params.contractId}`, { credentials: "include" });
        if (res.status === 401) { setLocation("/admin/login"); return; }
        if (res.status === 404) { setError("Contract not found in this organization."); return; }
        if (!res.ok) { setError("Failed to load contract."); return; }
        setData(await res.json() as ContractDetail);
      } catch {
        setError("Failed to load contract.");
      }
    })();
  }, [params.id, params.contractId, setLocation]);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link href={`/admin/orgs/${params.id}`} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to organization
        </Link>

        {error && (
          <div className="text-center py-12">
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-blue-400" />
                <div>
                  <h1 className="text-xl font-bold">
                    {data.coach?.name ?? "Unknown coach"} — {data.contract.contractType.replace(/_/g, " ")}
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Contract #{data.contract.id} · {data.contract.documentKind} · status {data.contract.status} · stage {data.contract.lifecycleState}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-800 text-sm">
                <div><p className="text-xs text-gray-500">Sport</p><p className="text-gray-200">{data.coach?.sport ?? "—"}</p></div>
                <div><p className="text-xs text-gray-500">Effective</p><p className="text-gray-200">{data.contract.startDate ?? "—"}</p></div>
                <div><p className="text-xs text-gray-500">Expires</p><p className="text-gray-200">{data.contract.endDate ?? "—"}</p></div>
                <div><p className="text-xs text-gray-500">Annual Salary</p><p className="text-gray-200">{data.contract.annualSalary != null ? `$${Number(data.contract.annualSalary).toLocaleString()}` : "—"}</p></div>
              </div>
            </div>

            {data.compliance && (
              <Section
                icon={<ShieldCheck className="h-4 w-4" />}
                title={`Policy Compliance${
                  data.compliance.summary
                    ? ` (${data.compliance.summary.pass ?? 0} pass · ${data.compliance.summary.warn ?? 0} warn · ${data.compliance.summary.fail ?? 0} fail)`
                    : ""
                }`}
              >
                {!data.compliance.ruleResults || data.compliance.ruleResults.length === 0 ? (
                  <Empty text="No active policy rules for this org." />
                ) : (
                  <ul className="divide-y divide-gray-800">
                    {data.compliance.ruleResults.map((r, i) => {
                      const color =
                        r.status === "fail"
                          ? "text-red-400"
                          : r.status === "warn"
                          ? "text-amber-400"
                          : "text-emerald-400";
                      return (
                        <li key={r.ruleId ?? i} className="py-3 px-5 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-200">{r.ruleName ?? r.type ?? "Rule"}</span>
                            <span className={`text-xs uppercase font-semibold ${color}`}>
                              {r.status}
                              {r.severity ? ` · ${r.severity}` : ""}
                            </span>
                          </div>
                          {(r.detail || r.message) && (
                            <p className="mt-1 text-xs text-gray-400">{r.detail ?? r.message}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Section>
            )}

            <Section icon={<BookOpen className="h-4 w-4" />} title={`Clauses (${data.clauses.length})`}>
              {data.clauses.length === 0 ? <Empty text="No clauses attached." /> : (
                <ul className="divide-y divide-gray-800">
                  {data.clauses.map((c) => (
                    <li key={c.id} className="py-3 px-5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-200 font-medium">{c.position + 1}. {c.title ?? `clause #${c.clauseId}`}</span>
                        <span className="text-xs text-gray-500 capitalize">{c.category ?? "—"}</span>
                      </div>
                      {c.body ? (
                        <div
                          className="mt-2 p-3 rounded bg-gray-950 border border-gray-800 text-xs text-gray-300 max-h-64 overflow-auto prose prose-invert prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.body) }}
                        />
                      ) : (
                        <p className="mt-2 text-xs text-gray-600 italic">No body text recorded for the attached clause version.</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={<MessageSquare className="h-4 w-4" />} title={`Negotiation Items (${data.negotiationItems.length})`}>
              {data.negotiationItems.length === 0 ? <Empty text="No open negotiation items." /> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-800">{["Raised by", "Summary", "Status", "Created"].map((h) => <th key={h} className="text-left px-4 py-2 text-xs text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.negotiationItems.map((n) => (
                      <tr key={n.id} className="border-b border-gray-800/50">
                        <td className="px-4 py-2 text-gray-200">{n.raisedBy}</td>
                        <td className="px-4 py-2 text-gray-300 max-w-md truncate">{n.summary}</td>
                        <td className="px-4 py-2 text-xs capitalize">{n.status}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{fmt(n.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            <Section icon={<Mail className="h-4 w-4" />} title={`Negotiation Rounds (${data.negotiationRounds.length})`}>
              {data.negotiationRounds.length === 0 ? <Empty text="No outbound rounds yet." /> : (
                <ul className="divide-y divide-gray-800">
                  {data.negotiationRounds.map((r) => (
                    <li key={r.id} className="py-2 px-5 flex justify-between text-xs">
                      <span className="text-gray-200">{r.direction} → {r.recipientRole}</span>
                      <span className="text-gray-500">{r.emailStatus} · {fmt(r.emailSentAt ?? r.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={<MessageSquare className="h-4 w-4" />} title={`Comments (${data.comments.length})`}>
              {data.comments.length === 0 ? <Empty text="No comments." /> : (
                <ul className="divide-y divide-gray-800">
                  {data.comments.map((c) => (
                    <li key={c.id} className="py-3 px-5 text-sm">
                      <p className="text-gray-200">{c.body}</p>
                      <p className="text-xs text-gray-500 mt-1">— {c.authorName} · {fmt(c.createdAt)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section icon={<Download className="h-4 w-4" />} title={`Files (${data.documents.length})`}>
              {data.documents.length === 0 ? <Empty text="No uploaded files." /> : (
                <ul className="divide-y divide-gray-800">
                  {data.documents.map((d) => {
                    const downloadUrl = `/api/admin/orgs/${params.id}/files/${d.id}/download`;
                    const previewUrl = `${downloadUrl}?inline=1`;
                    const isPdf = d.mimeType === "application/pdf";
                    const isImg = d.mimeType.startsWith("image/");
                    return (
                      <li key={d.id} className="py-3 px-5 text-sm">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="text-gray-200 truncate">{d.fileName}</p>
                            <p className="text-xs text-gray-500">{d.mimeType} · {Math.ceil(d.sizeBytes / 1024)} KB · uploaded {fmt(d.uploadedAt)}</p>
                          </div>
                          <a href={downloadUrl} className="text-xs text-blue-400 hover:underline ml-3 shrink-0" download>Download</a>
                        </div>
                        {isPdf && (
                          <iframe
                            src={previewUrl}
                            title={d.fileName}
                            className="mt-2 w-full h-[480px] rounded border border-gray-800 bg-white"
                          />
                        )}
                        {isImg && (
                          <img
                            src={previewUrl}
                            alt={d.fileName}
                            className="mt-2 max-w-full max-h-[480px] rounded border border-gray-800"
                          />
                        )}
                        {!isPdf && !isImg && (
                          <p className="mt-2 text-xs text-gray-500 italic">Inline preview not available for {d.mimeType} — use Download.</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            <Section icon={<ScrollText className="h-4 w-4" />} title={`Audit Log (last 50)`}>
              {data.auditLog.length === 0 ? <Empty text="No audit entries." /> : (
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-gray-800">{["When", "Actor", "Action", "Field", "Old → New"].map((h) => <th key={h} className="text-left px-4 py-2 text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.auditLog.map((a) => (
                      <tr key={a.id} className="border-b border-gray-800/50">
                        <td className="px-4 py-1.5 text-gray-500">{fmt(a.createdAt)}</td>
                        <td className="px-4 py-1.5 text-gray-300">{a.actorName ?? "—"}</td>
                        <td className="px-4 py-1.5 text-gray-200">{a.action}</td>
                        <td className="px-4 py-1.5 text-gray-400">{a.field ?? "—"}</td>
                        <td className="px-4 py-1.5 text-gray-400 max-w-md truncate">{a.oldValue ?? "—"} → {a.newValue ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
        {icon}<h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="px-5 py-3 text-sm text-gray-500">{text}</p>;
}
